import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import * as stylex from '@stylexjs/stylex';
import { Alert, Button, FieldError, FieldRow, Input, Label } from '@sentinel/ui';
import { sentinel } from '@sentinel/ui/theme/semantic.stylex';

const styles = stylex.create({
  card: {
    display: 'grid',
    gap: '0.875rem',
    paddingTop: '1.25rem',
    paddingBottom: '1.25rem',
    paddingLeft: '1.5rem',
    paddingRight: '1.5rem',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: `color-mix(in oklab, ${sentinel.textError} 40%, transparent)`,
    borderRadius: '0.625rem',
    backgroundColor: `color-mix(in oklab, ${sentinel.surfaceElevated} 60%, transparent)`,
  },
  header: { display: 'grid', gap: '0.25rem' },
  title: { margin: 0, fontSize: '1.0625rem', fontWeight: 600, color: sentinel.textError },
  subtitle: { margin: 0, fontSize: '0.875rem', color: sentinel.textMuted },
});

export interface DeleteCardProps {
  email: string;
  emailInput: string;
  password: string;
  pending: boolean;
  errorMessage: string | null;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onDeleteClick: () => void;
}

/** True iff the entered email matches the signed-in account (case-insensitive trim). */
export function emailMatches(input: string, signedInEmail: string): boolean {
  return input.trim().toLowerCase() === signedInEmail.trim().toLowerCase();
}

/**
 * Presentational danger-zone card. The page owns mutation state; this
 * component renders inputs, validation, and exposes the click handler. The
 * Delete button is disabled until the email confirmation matches the
 * signed-in user's email *and* a non-empty password is supplied — both
 * checks are belt-and-braces in addition to the backend's password check
 * and the global step-up middleware.
 */
export function DeleteCard({
  email,
  emailInput,
  password,
  pending,
  errorMessage,
  onEmailChange,
  onPasswordChange,
  onDeleteClick,
}: DeleteCardProps) {
  const { t } = useTranslation('data');
  const emailId = useId();
  const passwordId = useId();

  const showMismatch = emailInput.length > 0 && !emailMatches(emailInput, email);
  const canSubmit = !pending && emailMatches(emailInput, email) && password.length > 0;

  return (
    <section
      {...stylex.props(styles.card)}
      data-testid="delete-card"
      aria-labelledby="delete-card-title"
    >
      <header {...stylex.props(styles.header)}>
        <h2 id="delete-card-title" {...stylex.props(styles.title)}>
          {t('delete_card_title')}
        </h2>
        <p {...stylex.props(styles.subtitle)}>{t('delete_card_subtitle')}</p>
      </header>
      <Alert type="warning" role="alert" data-testid="delete-warning">
        {t('delete_warning')}
      </Alert>
      {errorMessage ? (
        <Alert type="error" role="alert">
          {errorMessage}
        </Alert>
      ) : null}
      <FieldRow>
        <Label htmlFor={emailId}>{t('delete_email_label')}</Label>
        <Input
          id={emailId}
          type="email"
          autoComplete="off"
          value={emailInput}
          placeholder={t('delete_email_placeholder')}
          onChange={(e) => onEmailChange(e.target.value)}
          aria-invalid={showMismatch ? true : undefined}
          aria-describedby={showMismatch ? `${emailId}-err` : undefined}
          data-testid="delete-email-input"
        />
        {showMismatch ? (
          <span id={`${emailId}-err`}>
            <FieldError message={t('delete_email_mismatch')} />
          </span>
        ) : null}
      </FieldRow>
      <FieldRow>
        <Label htmlFor={passwordId}>{t('delete_password_label')}</Label>
        <Input
          id={passwordId}
          type="password"
          autoComplete="current-password"
          value={password}
          placeholder={t('delete_password_placeholder')}
          onChange={(e) => onPasswordChange(e.target.value)}
          data-testid="delete-password-input"
        />
      </FieldRow>
      <div>
        <Button
          type="button"
          variant="danger"
          onClick={onDeleteClick}
          disabled={!canSubmit}
          data-testid="delete-button"
        >
          {t('delete_button')}
        </Button>
      </div>
    </section>
  );
}
